import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { addArea, getArea, getPlayer } from "@/sideEffects/api";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AreaPageFlow } from "./AreaPageFlow";
import { getEdges, getLayoutedElements, getNodes } from "./AreaPageUtils";
import { useEffect } from "react";

export const AreaPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const { data, isLoading, isFetching, isFetched } = useQuery({
    queryKey: ["area", activeFirm?.firmGuid],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
  });

  const {
    data: player,
    isLoading: isPlayerLoading,
    isFetching: isPlayerFetching,
  } = useQuery({
    queryKey: ["player", activeFirm?.firmGuid],
    queryFn: () =>
      getPlayer({ pageNumber: 1, numberOfObjectsPerPage: 999999 }, { Firm_Id: String(activeFirm?.firmGuid) }),
  });

  const areaData = data?.data?.result || [];
  const playerData = player?.data?.result || [];

  const queryClient = useQueryClient();
  const addFirstAreaMutation = useMutation({
    mutationFn: () =>
      addArea({
        firmGuid: String(activeFirm?.firmGuid),
        name: String(activeFirm?.firmName),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        areaTypeId: 0,
        overwriteSub: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["area"] });
      setTimeout(() => {
        document.location.reload();
      }, 1000);
    },
  });

  useEffect(() => {
    if (areaData.length === 0 && isFetched) {
      addFirstAreaMutation.mutate();
    }
  }, [areaData, isFetched]);

  useBreadcrumbs([{ name: t("entities.area"), path: ROUTES.AREA }]);

  if (isLoading || isFetching || isPlayerLoading || isPlayerFetching) return <LoadingSmall />;

  const { nodes, edges } = getLayoutedElements(getNodes(areaData, playerData), getEdges(areaData, playerData));

  return <AreaPageFlow layoutedNodes={nodes} layoutedEdges={edges} mode="edit" />;
};
